import { useRef, useEffect } from 'react';

/**
 * Hook to handle safe closing of modals (click-outside and Escape key).
 * Prevents closing if the user started a click/selection inside the modal
 * and released it outside (common issue when selecting text).
 * 
 * @param {function} onClose - Callback to close the modal
 * @returns {object} { overlayRef, onMouseDown, onMouseUp }
 */
export const useSafeClose = (onClose) => {
    const overlayRef = useRef(null);
    const mouseDownTarget = useRef(null);

    const onMouseDown = (e) => {
        mouseDownTarget.current = e.target;
    };

    const onMouseUp = (e) => {
        // Only trigger onClose if both mousedown and mouseup were on the overlay
        if (
            mouseDownTarget.current === overlayRef.current && 
            e.target === overlayRef.current
        ) {
            if (onClose) onClose();
        }
        mouseDownTarget.current = null;
    };

    // Listen for the Escape key to close the modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (onClose) onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    return {
        overlayRef,
        onMouseDown,
        onMouseUp
    };
};
